/**
 * DataGridRenderer — AG Grid 富交互表格渲染器
 *
 * 将 ```data-grid 代码块渲染为企业级可交互表格。
 * 支持：排序、冻结列、拖拽列宽、一键导出 CSV。
 *
 * AI 输出 JSON 格式：
 * {
 *   "columns": [{"field": "name", "headerName": "姓名", "sortable": true, "pinned": "left"}, ...],
 *   "rowData": [{"name": "张三", "amount": 50000}, ...]
 * }
 */

import React, { useState, useRef, useCallback, useMemo, useId } from 'react';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef, GridReadyEvent, GridApi } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

interface DataGridRendererProps {
    /** AI 输出的 JSON 字符串 */
    optionString: string;
}

const DataGridRenderer: React.FC<DataGridRendererProps> = ({ optionString }) => {
    const [error, setError] = useState<string>('');
    const gridApiRef = useRef<GridApi | null>(null);
    const uniqueId = useId().replace(/:/g, '_');

    // 解析 JSON
    const { columns, rowData } = useMemo(() => {
        try {
            const parsed = JSON.parse(optionString.trim());
            const cols: ColDef[] = (parsed.columns || []).map((col: any) => ({
                field: col.field,
                headerName: col.headerName || col.field,
                sortable: col.sortable !== false,
                resizable: true,
                filter: true,
                pinned: col.pinned || null,
                width: col.width || undefined,
                flex: col.flex || undefined,
            }));
            return { columns: cols, rowData: parsed.rowData || parsed.rows || [] };
        } catch (err: any) {
            setError(err.message);
            return { columns: [], rowData: [] };
        }
    }, [optionString]);

    const onGridReady = useCallback((params: GridReadyEvent) => {
        gridApiRef.current = params.api;
        params.api.sizeColumnsToFit();
    }, []);

    const handleExportCsv = useCallback(() => {
        gridApiRef.current?.exportDataAsCsv({ fileName: 'lawhelper-export.csv' });
    }, []);

    if (error) {
        return (
            <div style={{
                margin: '12px 0', padding: '16px', background: '#F9FAFB',
                border: '1px solid #E5E7EB', borderRadius: '8px', position: 'relative',
            }}>
                <div style={{ position: 'absolute', top: '8px', right: '12px', fontSize: '11px', color: '#9CA3AF', fontWeight: 600 }}>
                    DATA GRID
                </div>
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '13px', color: '#374151', fontFamily: 'monospace' }}>
                    {optionString}
                </pre>
            </div>
        );
    }

    const rowCount = rowData.length;
    const gridHeight = Math.min(Math.max(rowCount * 42 + 48, 200), 500);

    return (
        <div style={{
            margin: '16px 0', border: '1px solid #E5E7EB', borderRadius: '12px',
            overflow: 'hidden', background: '#FAFAFA', position: 'relative',
        }}>
            {/* 标题栏 */}
            <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '8px 12px', background: '#F3F4F6', borderBottom: '1px solid #E5E7EB',
            }}>
                <div style={{ fontSize: '11px', color: '#6B7280', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#10B981', display: 'inline-block' }} />
                    DATA GRID · {rowCount} 行
                </div>
                <button
                    onClick={handleExportCsv}
                    style={{
                        fontSize: '11px', color: '#3B82F6', background: 'transparent',
                        border: '1px solid #3B82F6', borderRadius: '4px', padding: '2px 8px',
                        cursor: 'pointer', fontWeight: 600,
                    }}
                >
                    导出 CSV ↓
                </button>
            </div>
            {/* AG Grid */}
            <div
                id={`data-grid-${uniqueId}`}
                className="ag-theme-alpine"
                style={{ height: `${gridHeight}px`, width: '100%' }}
            >
                <AgGridReact
                    columnDefs={columns}
                    rowData={rowData}
                    onGridReady={onGridReady}
                    domLayout="normal"
                    animateRows={true}
                    defaultColDef={{
                        sortable: true,
                        resizable: true,
                        filter: true,
                    }}
                />
            </div>
        </div>
    );
};

export default DataGridRenderer;
